import {
  STOCK_WRITE_OFF_STATUS_LABELS,
  STOCK_WRITE_OFF_STATUSES,
  type StockWriteOff,
  stockWriteOffStatusSchema,
  WRITE_OFF_REASON_LABELS,
  WRITE_OFF_REASONS,
  writeOffReasonSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { moneyExport } from "../../../../features/market/market-values.js";
import {
  type WriteOffFilters,
  useMarketPermissions,
  useWriteOffs,
  writeOffsQuery,
} from "../../../../features/market/queries.js";
import {
  LedgerStatus,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  useMarketStations,
  WriteOffStatusBadge,
} from "../../../../features/market/shared.js";
import { WriteOffDialog } from "../../../../features/market/write-off-dialog.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatCount, formatDate } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  status: stockWriteOffStatusSchema.optional().catch(undefined),
  reason: writeOffReasonSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/fireler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: WriteOffsRoute,
});

/**
 * Fire / imha belgeleri (Prompt 16 — web). Taslak → onaya gönder (onay motoru `STOCK_WRITE_OFF`; politika yoksa
 * hemen kaydedilir) → kaydedildi (stoktan düşer, kayıp fişi kesilir). Yazma `market.stock.manage`.
 */
function WriteOffsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketWriteOffs")}
      description={t("market.writeOffs.description")}
      breadcrumb={[{ label: t("menu.marketWriteOffs") }]}
    >
      {({ companyId }) => <WriteOffsBody companyId={companyId} />}
    </MarketPage>
  );
}

function WriteOffsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [creating, setCreating] = useState(false);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: WriteOffFilters = {
    stationId: station.stationId ?? undefined,
    status: search.status,
    reason: search.reason,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useWriteOffs(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StockWriteOff>[]>(
    () => [
      {
        id: "writeOffNo",
        header: t("market.writeOffs.fields.writeOffNo"),
        hideable: false,
        exportValue: (row) => row.writeOffNo,
        cell: (row) => (
          <Link
            to="/market/fireler/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-mono whitespace-nowrap">{row.writeOffNo}</span>
            <span className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</span>
          </Link>
        ),
      },
      {
        id: "station",
        header: t("market.fields.warehouse"),
        exportValue: (row) => `${row.stationName} · ${row.warehouseCode}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.stationName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.warehouseCode}</span>
          </span>
        ),
      },
      {
        id: "reason",
        header: t("market.writeOffs.fields.reason"),
        exportValue: (row) => WRITE_OFF_REASON_LABELS[row.reason],
        cell: (row) => WRITE_OFF_REASON_LABELS[row.reason],
      },
      {
        id: "lossKey",
        header: t("market.writeOffs.fields.lossKey"),
        defaultVisible: false,
        exportValue: (row) => t(`market.lossKey.${row.lossKey}`),
        cell: (row) => t(`market.lossKey.${row.lossKey}`),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => STOCK_WRITE_OFF_STATUS_LABELS[row.status],
        cell: (row) => <WriteOffStatusBadge status={row.status} />,
      },
      {
        id: "lineCount",
        header: t("market.fields.lineCount"),
        align: "right",
        exportValue: (row) => row.lineCount,
        cell: (row) => <span className="tabular">{formatCount(row.lineCount)}</span>,
      },
      {
        id: "totalValue",
        header: t("market.writeOffs.fields.totalValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.totalValue),
        cell: (row) => <MoneyText value={row.totalValue} className="font-medium" />,
      },
      {
        id: "ledger",
        header: t("market.fields.ledger"),
        exportValue: (row) => row.ledgerStatus,
        cell: (row) => <LedgerStatus status={row.ledgerStatus} journalEntryId={row.journalEntryId} />,
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        {permissions.canManageStock ? (
          <Button className="ms-auto" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("market.writeOffs.new")}
          </Button>
        ) : null}
      </Card>

      <DataTable
        caption={t("market.writeOffs.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Trash2 />}
            title={t("market.writeOffs.emptyTitle")}
            description={t("market.writeOffs.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setSearch({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {STOCK_WRITE_OFF_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {STOCK_WRITE_OFF_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("market.writeOffs.fields.reason")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={search.reason ?? ""}
                  onChange={(event) => setSearch({ reason: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {WRITE_OFF_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {WRITE_OFF_REASON_LABELS[reason]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StockWriteOff>(
            async (cursor) =>
              (
                await api.GET("/market/write-offs", {
                  params: { query: writeOffsQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "fire-imha",
            sheetName: t("menu.marketWriteOffs"),
            columns: visible,
            rows,
          });
        }}
      />

      {permissions.canManageStock ? (
        <WriteOffDialog
          open={creating}
          onOpenChange={setCreating}
          companyId={companyId}
          stations={station.stations}
          defaultStationId={station.stationId}
          onSaved={(saved) => void navigate({ to: "/market/fireler/$id", params: { id: saved.id } })}
        />
      ) : null}
    </>
  );
}
